import { Vector3 } from '@babylonjs/core';
import { InputManager } from '../core/InputManager';
import { PlayerPhysics } from './PlayerPhysics';
import { FirstPersonCamera } from './FirstPersonCamera';
import { ThirdPersonCamera } from './ThirdPersonCamera';

export type CameraMode = 'first-person' | 'third-person';

export interface PlayerControllerConfig {
  walkSpeed: number;
  runSpeed: number;
  crouchSpeed: number;
}

const DEFAULT_CONFIG: PlayerControllerConfig = {
  walkSpeed: 5,
  runSpeed: 9,
  crouchSpeed: 2.5,
};

export class PlayerController {
  private inputManager: InputManager;
  private physics: PlayerPhysics;
  private firstPersonCamera: FirstPersonCamera;
  private thirdPersonCamera: ThirdPersonCamera;

  private config: PlayerControllerConfig;
  private cameraMode: CameraMode = 'third-person';
  private isRunning: boolean = false;
  private isCrouching: boolean = false;

  // Player facing direction (follows movement/camera direction)
  private playerFacingYaw: number = 0;

  // Manual rotation with arrow keys (optional fine control)
  private readonly playerRotationSpeed: number = 2.5; // Radians per second

  constructor(
    physics: PlayerPhysics,
    firstPersonCamera: FirstPersonCamera,
    thirdPersonCamera: ThirdPersonCamera,
    config?: Partial<PlayerControllerConfig>
  ) {
    this.inputManager = InputManager.getInstance();
    this.physics = physics;
    this.firstPersonCamera = firstPersonCamera;
    this.thirdPersonCamera = thirdPersonCamera;
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Start in third person
    this.setCameraMode('third-person');
  }

  public update(deltaTime: number): void {
    this.handleCameraToggle();
    this.handleMouseLook();
    this.handlePlayerRotation(deltaTime);
    this.handleMovement();
    this.handleJump();
    this.handleZoom();

    // Update physics
    this.physics.update(deltaTime);

    // Update camera positions
    this.updateCameras();
  }

  private handleCameraToggle(): void {
    if (this.inputManager.isActionPressed('toggleCamera')) {
      this.setCameraMode(
        this.cameraMode === 'first-person' ? 'third-person' : 'first-person'
      );
    }
  }

  private handleMouseLook(): void {
    // Mouse rotation (when right button is held)
    const isRightButtonHeld = this.inputManager.isPointerLockedState();
    const delta = this.inputManager.getMouseDelta();

    if (!isRightButtonHeld) {
      return;
    }

    if (this.cameraMode === 'first-person') {
      this.firstPersonCamera.rotate(delta.x, delta.y);
    } else {
      this.thirdPersonCamera.rotate(delta.x, delta.y);
      const newYaw = this.thirdPersonCamera.getYaw();
      console.log(`[CAMERA] rotated by (${delta.x}, ${delta.y}), new yaw: ${(newYaw * 180 / Math.PI).toFixed(1)}°`);
    }
  }

  private handlePlayerRotation(deltaTime: number): void {
    // Arrow keys rotate the camera (which then affects movement direction)
    const rotation = this.inputManager.getRotationInput();

    if (rotation.x !== 0) {
      const rotationAmount = rotation.x * this.playerRotationSpeed * deltaTime;

      // Rotate the active camera
      if (this.cameraMode === 'first-person') {
        const currentYaw = this.firstPersonCamera.getYaw();
        this.firstPersonCamera.setYaw(currentYaw + rotationAmount);
      } else {
        const currentYaw = this.thirdPersonCamera.getYaw();
        this.thirdPersonCamera.setYaw(currentYaw + rotationAmount);
      }
    }
  }

  private handleMovement(): void {
    const movement = this.inputManager.getMovementVector();

    if (movement.x === 0 && movement.z === 0) {
      return;
    }

    // Get camera yaw for camera-relative movement
    const cameraYaw = this.cameraMode === 'first-person'
      ? this.firstPersonCamera.getYaw()
      : this.thirdPersonCamera.getYaw();

    console.log(`[MOVE] cameraYaw: ${(cameraYaw * 180 / Math.PI).toFixed(1)}°, input: (${movement.x}, ${movement.z})`);

    // Get camera-relative directions (movement follows camera direction)
    const forward = new Vector3(
      Math.sin(cameraYaw),
      0,
      Math.cos(cameraYaw)
    );

    const right = new Vector3(
      Math.cos(cameraYaw),
      0,
      -Math.sin(cameraYaw)
    );

    // Calculate movement direction relative to camera facing
    const moveDirection = new Vector3(
      forward.x * movement.z + right.x * movement.x,
      0,
      forward.z * movement.z + right.z * movement.x
    ).normalize();

    // Update player facing direction to match movement direction
    // This makes the player model rotate to face where they're moving
    this.playerFacingYaw = Math.atan2(moveDirection.x, moveDirection.z);

    // Determine speed
    this.isRunning = this.inputManager.isActionHeld('run');
    this.isCrouching = this.inputManager.isActionHeld('crouch');

    let speed = this.config.walkSpeed;
    if (this.isRunning && !this.isCrouching) {
      speed = this.config.runSpeed;
    } else if (this.isCrouching) {
      speed = this.config.crouchSpeed;
    }

    // Apply movement
    this.physics.applyMovement(moveDirection, speed);
  }

  private handleJump(): void {
    if (this.inputManager.isActionPressed('jump')) {
      this.physics.jump();
    }
  }

  private handleZoom(): void {
    if (this.cameraMode === 'third-person') {
      const wheelDelta = this.inputManager.getWheelDelta();
      if (wheelDelta !== 0) {
        this.thirdPersonCamera.zoom(wheelDelta);
      }
    }
  }

  private updateCameras(): void {
    const eyePos = this.physics.getEyePosition();
    const feetPos = this.physics.getFeetPosition();

    if (this.cameraMode === 'first-person') {
      this.firstPersonCamera.setPosition(eyePos);
    } else {
      this.thirdPersonCamera.setTarget(feetPos);
    }
  }

  public setCameraMode(mode: CameraMode): void {
    this.cameraMode = mode;

    if (mode === 'first-person') {
      // Sync yaw from third person to first person
      const yaw = this.thirdPersonCamera.getYaw();
      this.firstPersonCamera.setYaw(yaw);
      this.firstPersonCamera.setActive(true);
    } else {
      // Sync yaw from first person to third person
      const yaw = this.firstPersonCamera.getYaw();
      this.thirdPersonCamera.setYaw(yaw);
      this.thirdPersonCamera.setActive(true);
    }
  }

  public getCameraMode(): CameraMode {
    return this.cameraMode;
  }

  public getYaw(): number {
    // Return the player's facing direction (follows movement direction)
    return this.playerFacingYaw;
  }

  public getLookDirection(): Vector3 {
    if (this.cameraMode === 'first-person') {
      return this.firstPersonCamera.getLookDirection();
    } else {
      return this.thirdPersonCamera.getLookDirection();
    }
  }

  public isPlayerRunning(): boolean {
    return this.isRunning;
  }

  public isPlayerCrouching(): boolean {
    return this.isCrouching;
  }

  public isPlayerMoving(): boolean {
    const movement = this.inputManager.getMovementVector();
    return movement.x !== 0 || movement.z !== 0;
  }
}
